<template>
  <v-img
    v-bind="$attrs"
    :src="optimizedImageSrc || $attrs.src"
    :transition="false"
  />
</template>

<script setup>
import { computed, useAttrs } from 'vue'

import imageManifest from '@/assets/image-manifest.json'
import { useImageFormatSupport } from '@/composables/useImageFormatSupport'

// Get format support from the composable
const { avifSupported, webpSupported } = useImageFormatSupport()

// Define props for the component
const props = defineProps({
  // Force a specific format to be used ('avif', 'webp', or 'original')
  forceFormat: {
    type: String,
    default: null,
    validator: (value) => ['avif', 'webp', 'original', null].includes(value),
  },
})

// Component-specific state
const attrs = useAttrs()

// Handle imported assets which use relative paths
const extractPath = (path) => {
  // Skip processing if already a simple path format
  if (typeof path === 'string' && (path.startsWith('img/') || path.startsWith('/img/'))) {
    return path.startsWith('/') ? path : `/${path}`
  }

  // Handle webpack/vite processed imports which will be full URLs or absolute paths
  if (typeof path === 'string') {
    // Handle paths that come from imports (webpack/vite processed)
    if (path.includes('assets/')) {
      const parts = path.split('assets/')
      if (parts.length > 1) {
        return `/${parts[1]}`
      }
    }

    // Handle other absolute paths
    const imgMatch = path.match(/\/img\/(.+)$/)
    if (imgMatch) {
      return `/img/${imgMatch[1]}`
    }
  }

  return path
}

// Computed property for optimized image source
const optimizedImageSrc = computed(() => {
  // Get the original source and make sure it exists
  const originalSrc = attrs.src

  if (!originalSrc) {
    return null
  }

  // Get cleaned path for manifest lookup
  const cleanPath = extractPath(originalSrc)

  // Try multiple path formats to match the manifest
  const pathsToTry = [
    cleanPath, // /img/logo.png
    cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath, // img/logo.png (if starts with /)
    cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`, // /img/logo.png (if doesn't start with /)
  ]

  // Try all path formats
  let manifestEntry = null

  for (const path of pathsToTry) {
    if (imageManifest[path]) {
      manifestEntry = imageManifest[path]
      break
    }
  }

  if (!manifestEntry) {
    // If no optimized version found, always return original source
    return originalSrc
  }

  // If forceFormat is set, try to use that format
  if (props.forceFormat) {
    // Force AVIF (if available in manifest)
    if (props.forceFormat === 'avif' && manifestEntry.avif) {
      return manifestEntry.avif
    }

    // Force WebP (if available in manifest)
    if (props.forceFormat === 'webp' && manifestEntry.webp) {
      return manifestEntry.webp
    }

    // Force original
    if (props.forceFormat === 'original') {
      return originalSrc
    }

    // If forced format isn't available, fall back to original
    return originalSrc
  }

  // If no format is forced, use the best available format based on browser support
  // If AVIF is supported and available for this image, use it
  if (avifSupported.value && manifestEntry.avif) {
    return manifestEntry.avif
  }

  // If WebP is supported and available for this image, use it
  if (webpSupported.value && manifestEntry.webp) {
    return manifestEntry.webp
  }

  // Otherwise, use original source
  return originalSrc
})

// Make Vue ignore attributes by using defineOptions
defineOptions({
  inheritAttrs: false,
  name: 'ResponsiveImage',
})
</script>
